import { RuleActionType } from '@jupiterone/jupiter-types';
import { ChangeEvent, useState } from 'react';
import { JiraActionData, RuleActionData } from 'jupiter-web-alerts/src/types';

const DEFAULT_DESCRIPTION =
  "{{alertWebLink}}\n\n**Affected Items:**\n\n* {{queries.query0.data|mapProperty('displayName')|join('\n* ')}}";

const getDescriptionField = (text: string = DEFAULT_DESCRIPTION) => ({
  description: {
    type: 'doc',
    version: 1,
    content: [
      {
        type: 'paragraph',
        content: [
          {
            type: 'text',
            text,
          },
        ],
      },
    ],
  },
});

export interface JiraRuleConfigProps {
  onUpdate: (newValue: RuleActionData) => void;
  disabled?: boolean;
  initialActionData?: JiraActionData;
}

interface InternalActionState extends JiraActionData {
  description: string;
}

// Exported for tests
export const DEFAULT_ACTION_DATA = {
  type: RuleActionType.CREATE_JIRA_TICKET,
  summary: '',
  issueType: '',
  entityClass: '',
  integrationInstanceId: '',
  additionalFields: getDescriptionField(),
  project: '',
};

const useJiraRuleConfigLogic = ({
  disabled,
  initialActionData = DEFAULT_ACTION_DATA,
  onUpdate,
}: JiraRuleConfigProps) => {
  const [
    internalActionState,
    setInternalActionState,
  ] = useState<InternalActionState>(
    getStateFromInitialActionData(initialActionData)
  );
  const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
    const newState = {
      ...internalActionState,
      [e.target.name]: e.target.value,
    };
    setInternalActionState(newState);

    const {
      summary,
      issueType,
      entityClass,
      integrationInstanceId,
      additionalFields,
      description,
      project,
    } = newState; // Destruction sanitation
    const action: JiraActionData = {
      ...initialActionData,
      type: RuleActionType.CREATE_JIRA_TICKET,
      summary,
      issueType,
      integrationInstanceId,
      entityClass,
      additionalFields: {
        ...additionalFields,
        ...getDescriptionField(description),
      },
      project,
    };
    onUpdate(action);
  };

  return {
    internalActionState,
    disabled,
    jsonValue: internalActionState.additionalFields
      ? {
          ...internalActionState.additionalFields,
          description: undefined,
        }
      : {},
    handleChangeValue,
  };
};
export default useJiraRuleConfigLogic;

const getStateFromInitialActionData = ({
  additionalFields,
  ...data
}: JiraActionData): InternalActionState => {
  const additionalFieldsWithoutDescription = {
    ...additionalFields,
    description: undefined,
  };
  const description =
    additionalFields?.description?.content?.[0].content?.[0].text;
  const state = {
    ...data,
    description,
    additionalFields: additionalFieldsWithoutDescription,
  };
  return state;
};
